import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiArrowLeft, HiPlus, HiX, HiTrash } from 'react-icons/hi';
import { useAuthHeaders } from '../../hooks/useAuthHeaders';
import { useAuth } from '../../contexts/AuthContext';
import './NCAdmin.css';

export default function AdminGames({ config }) {
  const [games, setGames] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState({});
  const { getAuthHeaders } = useAuthHeaders();
  const { user } = useAuth();

  useEffect(() => { loadGames(); }, []);
  useEffect(() => { if (user) loadUsers(); }, [user]);

  const loadGames = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games`);
      const data = await res.json();
      setGames(data.data.games || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${config.apiUrl}/users`, { headers });
      const data = await res.json();
      setUsers((data.data || []).filter(u => ['admin', 'streamer'].includes(u.role)));
    } catch (e) { console.error(e); }
  };

  const REQUIRED_FIELDS = ['sport', 'homeTeam', 'awayTeam', 'venue', 'scheduledStart', 'scheduledEnd'];

  const createGame = async (e) => {
    e.preventDefault();
    setError('');
    const form = e.target;
    const errors = {};
    REQUIRED_FIELDS.forEach(f => { if (!form[f].value) errors[f] = 'Required'; });
    if (form.scheduledStart.value && form.scheduledEnd.value &&
        form.scheduledEnd.value <= form.scheduledStart.value) {
      errors.scheduledEnd = 'Must be after start time';
    }
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});

    const game = {
      sport: form.sport.value, homeTeam: form.homeTeam.value,
      awayTeam: form.awayTeam.value, venue: form.venue.value,
      field: form.field.value,
      scheduledStart: new Date(form.scheduledStart.value).toISOString(),
      scheduledEnd: new Date(form.scheduledEnd.value).toISOString(),
      streamerId: form.streamerId.value || undefined,
    };
    try {
      setCreating(true);
      const headers = await getAuthHeaders();
      const res = await fetch(`${config.apiUrl}/games`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(game),
      });
      if (res.ok) { form.reset(); setShowForm(false); loadGames(); }
      else { const err = await res.json(); setError(err.message); }
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const assignStreamer = async (gameId, streamerId) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(`${config.apiUrl}/games/${gameId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ streamerId: streamerId || null }),
      });
      loadGames();
    } catch (e) { console.error(e); }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Delete this game?')) return;
    setDeleting(d => ({ ...d, [gameId]: true }));
    try {
      const headers = await getAuthHeaders();
      await fetch(`${config.apiUrl}/games/${gameId}`, { method: 'DELETE', headers });
      loadGames();
    } catch (e) { console.error(e); }
    finally { setDeleting(d => ({ ...d, [gameId]: false })); }
  };

  if (loading) return (
    <div className="nc-admin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loading-spinner" />
    </div>
  );

  return (
    <div className="nc-admin">
      <Link to="/admin" className="nc-admin-back"><HiArrowLeft /> Admin</Link>

      <div className="nc-admin-header">
        <div>
          <div className="nc-admin-eyebrow">NC LIVE</div>
          <h1 className="nc-admin-title">Games</h1>
        </div>
        <button className={`nc-admin-btn ${showForm ? 'ghost' : ''}`} onClick={() => setShowForm(!showForm)}>
          {showForm ? <><HiX /> Cancel</> : <><HiPlus /> Create Game</>}
        </button>
      </div>

      {showForm && (
        <div className="nc-admin-form-panel">
          <h2>New Game</h2>
          {error && <div className="nc-admin-alert">{error}</div>}
          <form onSubmit={createGame}>
            <div className="nc-admin-form-grid">
              <div className="nc-admin-field">
                <label>Sport</label>
                <select name="sport" className={fieldErrors.sport ? 'has-error' : ''}>
                  <option value="">Select sport...</option>
                  {['Basketball','Football','Rugby','Swimming','Tennis','Track & Field','Volleyball'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {fieldErrors.sport && <span className="nc-admin-field-error">{fieldErrors.sport}</span>}
              </div>
              {[
                { name: 'venue', label: 'Venue', placeholder: 'e.g. Nudgee Oval' },
                { name: 'homeTeam', label: 'Home Team' },
                { name: 'awayTeam', label: 'Away Team' },
                { name: 'field', label: 'Field / Court (optional)' },
                { name: 'scheduledStart', label: 'Start Time', type: 'datetime-local' },
                { name: 'scheduledEnd', label: 'End Time', type: 'datetime-local' },
              ].map(f => (
                <div className="nc-admin-field" key={f.name}>
                  <label>{f.label}</label>
                  <input name={f.name} type={f.type || 'text'} placeholder={f.placeholder || ''}
                    className={fieldErrors[f.name] ? 'has-error' : ''} />
                  {fieldErrors[f.name] && <span className="nc-admin-field-error">{fieldErrors[f.name]}</span>}
                </div>
              ))}
              <div className="nc-admin-field">
                <label>Assign Streamer (optional)</label>
                <select name="streamerId">
                  <option value="">Unassigned</option>
                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>{u.givenName} {u.familyName}</option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" className="nc-admin-btn" disabled={creating}>
              {creating
                ? <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> Creating...</>
                : 'Create Game'}
            </button>
          </form>
        </div>
      )}

      {games.length === 0 ? (
        <div className="nc-admin-empty">No games yet — create one above</div>
      ) : (
        <div className="nc-admin-games">
          {games.map(game => (
            <div key={game.gameId} className="nc-admin-game-row">
              <span className={`nc-admin-status ${game.status}`}>{game.status}</span>
              <span className="nc-admin-game-sport">{game.sport}</span>
              <div className="nc-admin-game-teams">{game.homeTeam} vs {game.awayTeam}</div>
              <div className="nc-admin-game-meta">
                {game.venue}{game.field ? ` — ${game.field}` : ''}<br />
                {new Date(game.scheduledStart).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
              <div className="nc-admin-game-streamer">
                <select
                  value={game.streamerId || ''}
                  onChange={e => assignStreamer(game.gameId, e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>{u.givenName} {u.familyName}</option>
                  ))}
                </select>
              </div>
              <div className="nc-admin-game-actions">
                <button onClick={() => deleteGame(game.gameId)} className="nc-admin-btn danger" disabled={deleting[game.gameId]}>
                  {deleting[game.gameId]
                    ? <div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                    : <HiTrash />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
